import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { NAV_LINKS, CONTACT } from "@/data/site";
import { scrollToHash, scrollAfterNavigation } from "@/lib/scroll";
import { track } from "@/lib/track";
import { useLang } from "@/i18n/LanguageContext";

// Social labels stay hidden until real profile URLs are confirmed — flip to true and add hrefs.
const SHOW_SOCIALS = false;
const SOCIALS = ["Instagram", "TikTok"];

const Footer = () => {
  const { t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const go = (hash) => (e) => {
    e.preventDefault();
    if (hash === "#top") {
      if (pathname !== "/") navigate("/");
      else scrollToHash("body");
      return;
    }
    if (pathname !== "/") {
      navigate("/");
      scrollAfterNavigation(hash);
    } else {
      scrollToHash(hash);
    }
  };

  return (
  <footer
    id="contact"
    data-testid="site-footer"
    className="border-t border-gold/15 bg-[#0a0a0a]"
  >
    <div className="mx-auto max-w-[1680px] px-6 py-20 lg:px-12 lg:py-28">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Logo variant="nav" />
          <p className="mt-8 max-w-xs font-sans text-sm font-light leading-relaxed text-ivory/50">
            {t.footer.blurb}
          </p>
          {SHOW_SOCIALS && (
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <span
                  key={s}
                  data-testid={`footer-social-${s.toLowerCase()}`}
                  className="font-sans text-[11px] tracking-[0.25em] text-ivory/50"
                >
                  {s.toUpperCase()}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          <p className="font-sans text-[11px] tracking-[0.35em] text-gold">{t.footer.explore}</p>
          <nav className="mt-6 flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={l.hash}
                onClick={go(l.hash)}
                data-testid={`footer-nav-${l.id}`}
                className="font-sans text-sm font-light text-ivory/60 transition-colors duration-300 hover:text-gold"
              >
                {t.nav[l.id]}
              </a>
            ))}
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="font-sans text-sm font-light text-ivory/60 transition-colors duration-300 hover:text-gold"
            >
              {t.footer.privacy}
            </Link>
          </nav>
        </div>

        <div className="lg:col-span-3">
          <p className="font-sans text-[11px] tracking-[0.35em] text-gold">{t.footer.contact}</p>
          <div className="mt-6 flex flex-col gap-3 font-sans text-sm font-light text-ivory/60">
            <a
              href={`mailto:${CONTACT.email}`}
              onClick={() => track("email_click", { location: "footer" })}
              data-testid="footer-email"
              className="py-1 transition-colors duration-300 hover:text-gold"
            >
              {CONTACT.email}
            </a>
            <span data-testid="footer-location">{CONTACT.city}</span>
          </div>
        </div>
      </div>

      <div className="mt-20 flex flex-col gap-4 border-t border-gold/10 py-8 md:flex-row md:items-center md:justify-between">
        <p className="font-sans text-[11px] tracking-[0.15em] text-ivory/35">
          {t.footer.rights}
        </p>
        <Link
          to="/privacy"
          data-testid="footer-bottom-privacy-link"
          className="font-sans text-[11px] tracking-[0.15em] text-ivory/35 transition-colors duration-300 hover:text-gold"
        >
          {t.footer.privacy}
        </Link>
        <p className="font-sans text-[11px] tracking-[0.15em] text-ivory/35">
          {t.footer.dryHire}
        </p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
